// =====================================================================
// src/lib/topo.js — a decisão dos dois botões do canto, sem DOM
//
// Os dois botões (sino e sol/lua) são cópia do app do Monteiro de Moraes
// (`#top-controls`, index.html linha 542). O que está AQUI é só o que dá
// para decidir sem navegador, e por isso dá para provar em node:
//
//   temaInicial()   qual tema a tela abre, lendo o que foi guardado
//   proximoTema()   o que o clique produz
//   textoDoBadge()  o que o número em cima do sino mostra
//   ordenar()       a ordem em que os avisos aparecem
//
// A CHAVE E A CLASSE JÁ EXISTIAM (`sgh.tema`, `body.dark-mode`, em
// src/main.js desde que a casca foi feita). Não as troquei: mudar o nome
// da chave faria todo mundo que já escolheu o escuro voltar ao claro, de
// graça. O que este arquivo acrescenta é a troca do ícone e o badge.
// =====================================================================

export const CHAVE_TEMA = 'sgh.tema';

// Um valor guardado que não seja 'escuro' vale como claro — inclusive
// lixo, `null` e string vazia. Tema é preferência, não configuração: na
// dúvida abre claro, que é o que a equipe do hotel usa de dia.
export function temaInicial(guardado) {
  return guardado === 'escuro' ? 'escuro' : 'claro';
}

export function proximoTema(atual) {
  return atual === 'escuro' ? 'claro' : 'escuro';
}

// O ícone mostra PARA ONDE o clique leva, não onde você está: no claro
// aparece a lua (clique e escurece), no escuro aparece o sol. É assim no
// MdM (`_SVG_SOL` / `_SVG_LUA`, linha 18372) e é o que todo mundo espera.
export function iconeDoTema(atual) {
  return atual === 'escuro' ? 'ic-sol' : 'ic-lua';
}

// "9+" a partir de dez, como no MdM. Zero não vira '0': vira nada, e o
// badge some — um zero vermelho no canto da tela é ansiedade sem motivo.
export function textoDoBadge(quantos) {
  if (!quantos || quantos <= 0) return '';
  return quantos > 9 ? '9+' : String(quantos);
}

// A ordem é a do banco (`avisos()` devolve `prioridade`), e o desempate é
// a chave — para a lista não dançar entre duas aberturas do painel com os
// mesmos avisos.
export function ordenar(avisos) {
  return [...(avisos || [])].sort((a, b) =>
    (a.prioridade - b.prioridade) || String(a.chave).localeCompare(String(b.chave)));
}

// =====================================================================
// A ABA DOS ÚLTIMOS 30 DIAS (0036)
//
// A LISTA NÃO SE REORDENA AQUI. `historico_de_avisos()` já devolve do mais
// recente para o mais antigo, e a cor de cada linha vem pronta do banco. A
// tela desenha o que chegou — se ela recalculasse o estado, passaria a
// existir uma segunda opinião sobre o que está resolvido, e um dia as duas
// discordariam. O que mora aqui é só o que é de apresentação.
// =====================================================================

// "Hoje 14:32" é mais útil que "19/09 14:32" para quem está olhando agora;
// a data cheia só aparece quando o ano é outro. Mesma escada do MdM.
export function rotuloDaData(quando, agora = new Date()) {
  if (!quando) return '';
  const d = quando instanceof Date ? quando : new Date(quando);
  if (isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  const dia = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const passados = Math.round((dia(agora) - dia(d)) / 86400000);
  if (passados === 0) return 'Hoje ' + hh;
  if (passados === 1) return 'Ontem ' + hh;
  const dm = String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0');
  return d.getFullYear() === agora.getFullYear() ? dm + ' ' + hh : dm + '/' + d.getFullYear();
}

// O cabeçalho da aba diz o que a pessoa quer saber antes de ler a lista:
// quantos ainda esperam alguém. Encerrado não entra na frase — quem lê
// "3 pendentes" está decidindo o que fazer hoje, não conferindo arquivo.
export function contarPorEstado(lista) {
  const c = { pendente: 0, resolvido: 0, encerrado: 0 };
  for (const h of lista || []) if (h && c[h.estado] !== undefined) c[h.estado]++;
  return c;
}

export function resumoDoHistorico(lista) {
  const c = contarPorEstado(lista);
  if (!lista || lista.length === 0) return 'Nada nos últimos 30 dias.';
  if (c.pendente === 0) return 'Nada pendente nos últimos 30 dias.';
  return c.pendente === 1 ? '1 ainda esperando alguém' : `${c.pendente} ainda esperando alguém`;
}
